import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash2, Search, Star, Wallet, Calendar, Code, TrendingUp, CreditCard, Sparkles } from "lucide-react";
import api, { formatTRY, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";
import Pagination from "@/components/Pagination";

const PAGE_SIZE = 50;

export default function AdminHotelsPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [hotels, setHotels] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [destinations, setDestinations] = useState([]);
    const [filterDest, setFilterDest] = useState("");
    const [yieldImpact, setYieldImpact] = useState({}); // hotel_id -> {fires, avg_delta_pct, est_revenue_lift}

    const loadYieldImpact = async (hotelIds) => {
        if (!hotelIds.length) return;
        try {
            const r = await api.get("/admin/yield/impact-summary", { params: { hotel_ids: hotelIds.join(","), hours: 24 } });
            const map = {};
            (r.data.items || []).forEach((it) => { map[it.hotel_id] = it; });
            setYieldImpact(map);
        } catch (e) {
            // Soft-fail — badge is non-critical
        }
    };

    const load = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (filterDest) params.set("destination", filterDest);
        params.set("limit", PAGE_SIZE);
        params.set("skip", (page - 1) * PAGE_SIZE);
        api.get(`/admin/hotels?${params.toString()}`)
            .then((r) => {
                setHotels(r.data.items);
                setTotal(r.data.total);
                loadYieldImpact((r.data.items || []).map((h) => h.id));
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/login");
        api.get("/admin/destinations").then((r) => setDestinations(r.data));
        load();
    }, [user, authLoading]); // eslint-disable-line

    useEffect(() => {
        const t = setTimeout(load, 350);
        return () => clearTimeout(t);
    }, [q, filterDest, page]); // eslint-disable-line

    // Filter değişince sayfayı 1'e döndür
    useEffect(() => { setPage(1); }, [q, filterDest]);

    const onDelete = async (id, name) => {
        if (!window.confirm(`'${name}' otelini silmek istediğinizden emin misiniz?`)) return;
        try {
            await api.delete(`/admin/hotels/${id}`);
            setHotels((prev) => prev.filter((h) => h.id !== id));
        } catch (e) {
            alert(e.response?.data?.detail || "Silme başarısız");
        }
    };

    const onToggleFeatured = async (id) => {
        try {
            const { data } = await api.post(`/admin/hotels/${id}/toggle-featured`);
            setHotels((prev) => prev.map((h) => h.id === id ? { ...h, is_featured: data.is_featured } : h));
        } catch (e) {
            alert(e.response?.data?.detail || "İşlem başarısız");
        }
    };

    return (
        <AdminLayout
            title="Oteller"
            action={
                <Link data-testid="new-hotel-btn" to="/admin/hotels/new" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold">
                    <Plus className="h-4 w-4" /> Yeni Otel
                </Link>
            }
        >
            <div className="flex flex-wrap items-center gap-3 mb-5">
                <div className="flex items-center gap-2 flex-1 min-w-[280px] px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                    <Search className="h-4 w-4 text-slate-400" />
                    <input data-testid="hotel-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Otel adı ara..." className="w-full bg-transparent outline-none text-sm text-slate-900 dark:text-white" />
                </div>
                <select data-testid="filter-destination" value={filterDest} onChange={(e) => setFilterDest(e.target.value)} className="px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm text-slate-900 dark:text-white">
                    <option value="">Tüm destinasyonlar</option>
                    {destinations.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}
                </select>
                <span className="text-sm text-slate-500">{total} otel</span>
            </div>

            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30">
                                <th className="py-3 px-4">Otel</th>
                                <th className="py-3 px-4">Destinasyon</th>
                                <th className="py-3 px-4">Yıldız</th>
                                <th className="py-3 px-4">Puan</th>
                                <th className="py-3 px-4">Konsept</th>
                                <th className="py-3 px-4">Fiyat</th>
                                <th className="py-3 px-4">Oda</th>
                                <th className="py-3 px-4 text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="8" className="py-8 text-center text-slate-500">Yükleniyor...</td></tr>
                            ) : hotels.length === 0 ? (
                                <tr><td colSpan="8" className="py-8 text-center text-slate-500">Otel bulunamadı.</td></tr>
                            ) : hotels.map((h) => (
                                <tr key={h.id} data-testid={`admin-hotel-${h.id}`} className="border-b border-slate-100 dark:border-slate-800/40 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                    <td className="py-3 px-4">
                                        <div className="flex items-center gap-3">
                                            <img src={resolveImageUrl(h.main_image)} alt="" className="h-10 w-14 object-cover rounded-lg flex-shrink-0" />
                                            <div className="min-w-0">
                                                <div className="font-semibold text-slate-900 dark:text-white truncate max-w-[260px]">{h.name}</div>
                                                <div className="text-xs text-slate-500 flex items-center gap-2">
                                                    {h.is_featured && <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 text-[10px] font-bold">FEATURED</span>}
                                                    {h.is_deal && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold">DEAL</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-3 px-4 text-slate-700 dark:text-slate-300">{h.destination_name}</td>
                                    <td className="py-3 px-4">
                                        <div className="flex items-center gap-0.5">{[...Array(h.stars)].map((_, i) => <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />)}</div>
                                    </td>
                                    <td className="py-3 px-4"><span className="font-bold text-sky-600">{h.rating}</span></td>
                                    <td className="py-3 px-4 text-slate-700 dark:text-slate-300">
                                        {(h.boards && h.boards.length > 0) ? (
                                            <div className="flex flex-wrap gap-1">
                                                {h.boards.map((b, i) => (
                                                    <span key={i} data-testid={`hotel-board-${h.id}-${i}`} className="inline-block px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 text-[10px] font-semibold border border-emerald-200/60 dark:border-emerald-800/40">{b}</span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-slate-400 italic">— Plan yok</span>
                                        )}
                                    </td>
                                    <td className="py-3 px-4 font-semibold text-slate-900 dark:text-white">{formatTRY(h.price_from)}</td>
                                    <td className="py-3 px-4 text-slate-700 dark:text-slate-300">{h.room_types?.length || 0}</td>
                                    <td className="py-3 px-4">
                                        <div className="flex items-center justify-end gap-1">
                                            <Link data-testid={`rates-hotel-${h.id}`} to={`/admin/hotels/${h.id}/rates`} className="p-2 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 transition-colors" title="Fiyat Planları">
                                                <Wallet className="h-4 w-4" />
                                            </Link>
                                            <Link data-testid={`calendar-hotel-${h.id}`} to={`/admin/hotels/${h.id}/calendar`} className="p-2 rounded-lg hover:bg-sky-50 dark:hover:bg-sky-950/30 text-sky-600 dark:text-sky-400 transition-colors" title="Takvim & Stok">
                                                <Calendar className="h-4 w-4" />
                                            </Link>
                                            <Link data-testid={`widget-hotel-${h.id}`} to={`/admin/hotels/${h.id}/widget`} className="p-2 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 transition-colors" title="Web Sitesi Widget'ı (Embed)">
                                                <Code className="h-4 w-4" />
                                            </Link>
                                            <Link data-testid={`pos-hotel-${h.id}`} to={`/admin/hotels/${h.id}/pos-config`} className="p-2 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-950/30 text-amber-600 dark:text-amber-400 transition-colors" title="POS Ayarları">
                                                <CreditCard className="h-4 w-4" />
                                            </Link>
                                            <Link data-testid={`promo-hotel-${h.id}`} to={`/admin/hotels/${h.id}/promotions`} className="p-2 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/30 text-rose-600 dark:text-rose-400 transition-colors" title="Promosyonlar">
                                                <Sparkles className="h-4 w-4" />
                                            </Link>
                                            <Link data-testid={`yield-hotel-${h.id}`} to={`/admin/hotels/${h.id}/yield`} className="relative p-2 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-950/30 text-purple-600 dark:text-purple-400 transition-colors" title="Yield Manager (Dinamik Fiyatlandırma)">
                                                <TrendingUp className="h-4 w-4" />
                                                {yieldImpact[h.id] && yieldImpact[h.id].fires > 0 && (
                                                    <span
                                                        data-testid={`yield-impact-${h.id}`}
                                                        className={`absolute -top-1 -right-1 px-1 min-w-[18px] h-[16px] rounded-full text-[9px] font-bold leading-[16px] text-center whitespace-nowrap ${yieldImpact[h.id].est_revenue_lift >= 0 ? "bg-emerald-500 text-white" : "bg-rose-500 text-white"}`}
                                                        title={`Son 24s · ${yieldImpact[h.id].fires} kez tetiklendi · ${yieldImpact[h.id].cells_affected} hücre · ort. ${yieldImpact[h.id].avg_delta_pct >= 0 ? "+" : ""}${yieldImpact[h.id].avg_delta_pct}% · tahmini ${yieldImpact[h.id].est_revenue_lift >= 0 ? "+" : ""}${formatTRY(yieldImpact[h.id].est_revenue_lift)} gelir etkisi`}
                                                    >
                                                        {yieldImpact[h.id].avg_delta_pct >= 0 ? "+" : ""}{Math.round(yieldImpact[h.id].avg_delta_pct)}%
                                                    </span>
                                                )}
                                            </Link>
                                            <button
                                                data-testid={`feature-hotel-${h.id}`}
                                                onClick={() => onToggleFeatured(h.id)}
                                                title={h.is_featured ? "Öne çıkarmayı kaldır" : "Öne çıkar"}
                                                className={`p-2 rounded-lg transition-colors ${h.is_featured ? "bg-amber-100 dark:bg-amber-950/40 text-amber-600" : "hover:bg-amber-50 dark:hover:bg-amber-950/30 text-slate-400 hover:text-amber-600"}`}
                                            >
                                                <Star className={`h-4 w-4 ${h.is_featured ? "fill-amber-500" : ""}`} />
                                            </button>
                                            <Link data-testid={`edit-hotel-${h.id}`} to={`/admin/hotels/${h.id}/edit`} className="p-2 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-950/30 text-amber-700 dark:text-amber-400 transition-colors" title="Otel Bilgilerini Düzenle">
                                                <Edit2 className="h-4 w-4" />
                                            </Link>
                                            <button data-testid={`delete-hotel-${h.id}`} onClick={() => onDelete(h.id, h.name)} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600 transition-colors" title="Sil">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <Pagination total={total} page={page} pageSize={PAGE_SIZE} onChange={setPage} label="otel" />
            </div>
        </AdminLayout>
    );
}
